import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useAuth } from '../contexts/AuthContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { useWallet } from '../contexts/WalletContext'
import { getImageUrl } from '../utils/cdnUtils'
import { getPreferredUserIdentifier } from '../utils/userIdentity'
import {
  getActiveRewards,
  getPointsConfig,
  notifyUserSummaryUpdated,
  redeemReward,
  type PointsConfigData,
  type PointsReward,
} from '../services/pointsService'

const getCurrentPoints = (userInfo: ReturnType<typeof useUserInfo>['userInfo']): number => {
  if (!userInfo) return 0
  const loyaltyPoints = Number(userInfo.loyaltyPoints?.currentPoints)
  if (Number.isFinite(loyaltyPoints)) return loyaltyPoints
  const bonusPoints = Number((Array.isArray(userInfo.bonuses) ? userInfo.bonuses[0]?.points : 0) || 0)
  return Number.isFinite(bonusPoints) ? bonusPoints : 0
}

const formatNumber = (value: number) => Number(value || 0).toLocaleString('th-TH')

const PointsRewardsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { walletData, formatCurrency, refreshWallet } = useWallet()
  const { userInfo, refreshUserInfo } = useUserInfo()
  const [pointsConfig, setPointsConfig] = useState<PointsConfigData | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [submittingRewardId, setSubmittingRewardId] = useState<string | null>(null)

  const tenantId = user?.tenantId || userInfo?.tenantId || ''
  const points = getCurrentPoints(userInfo)
  const pointsText = `${points.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pts`
  const balanceText = walletData ? `${formatCurrency(walletData.balance.main)} บาท` : '0.00 บาท'

  useEffect(() => {
    let mounted = true
    const loadConfig = async () => {
      if (!tenantId) {
        if (mounted) setIsLoadingConfig(false)
        return
      }
      try {
        const config = await getPointsConfig(tenantId)
        if (mounted) setPointsConfig(config)
      } finally {
        if (mounted) setIsLoadingConfig(false)
      }
    }
    void loadConfig()
    return () => {
      mounted = false
    }
  }, [tenantId])

  const rewardsEnabled = pointsConfig?.system?.rewardsEnabled !== false
  const activeRewards = useMemo(() => getActiveRewards(pointsConfig?.rewards), [pointsConfig?.rewards])

  const handleRedeem = async (reward: PointsReward) => {
    if (!tenantId || !user) {
      await Swal.fire({ icon: 'error', title: 'ไม่พบข้อมูลผู้ใช้', confirmButtonColor: '#d33' })
      return
    }
    const authIdentity = user as unknown as { _id?: string | null; id?: string | null; userId?: string | null }
    const identifier = getPreferredUserIdentifier({
      _id: userInfo?._id || authIdentity?._id,
      id: authIdentity?.id,
      userId: userInfo?.userId || authIdentity?.userId,
    })
    if (!identifier) {
      await Swal.fire({ icon: 'error', title: 'ไม่พบรหัสผู้ใช้', confirmButtonColor: '#d33' })
      return
    }

    let shippingInfo: Record<string, unknown> | null = null
    if (reward.type === 'item') {
      const shipping = await Swal.fire({
        icon: 'question',
        title: 'ข้อมูลสำหรับจัดส่ง/รับรางวัล',
        html: `
          <input id="reward-contact-name" class="swal2-input" placeholder="ชื่อผู้รับ">
          <input id="reward-contact-phone" class="swal2-input" placeholder="เบอร์ติดต่อ">
          <textarea id="reward-contact-address" class="swal2-textarea" placeholder="ที่อยู่/หมายเหตุ"></textarea>
        `,
        showCancelButton: true,
        confirmButtonText: 'ยืนยันข้อมูล',
        cancelButtonText: 'ยกเลิก',
        confirmButtonColor: '#d33',
        preConfirm: () => {
          const name = (document.getElementById('reward-contact-name') as HTMLInputElement | null)?.value?.trim()
          const phone = (document.getElementById('reward-contact-phone') as HTMLInputElement | null)?.value?.trim()
          const address = (document.getElementById('reward-contact-address') as HTMLTextAreaElement | null)?.value?.trim()
          if (!name || !phone) {
            Swal.showValidationMessage('กรุณาระบุชื่อผู้รับและเบอร์ติดต่อ')
            return null
          }
          return { name, phone, address }
        },
      })
      if (!shipping.isConfirmed || !shipping.value) return
      shippingInfo = shipping.value
    }

    const confirmed = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการแลกรางวัล',
      html: `ใช้ <strong>${formatNumber(reward.pointsCost)}</strong> คะแนน เพื่อแลก <strong>${reward.name}</strong>${
        reward.type === 'credit' ? ` (เครดิต ${formatNumber(reward.creditAmount)} บาท)` : ''
      }`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d33',
    })

    if (!confirmed.isConfirmed) return

    try {
      setSubmittingRewardId(reward.id)
      const response = await redeemReward({
        tenantId,
        userId: identifier,
        rewardId: reward.id,
        shippingInfo,
      })
      notifyUserSummaryUpdated()
      await Promise.all([refreshWallet(), refreshUserInfo()])
      await Swal.fire({
        icon: 'success',
        title: response.message || 'แลกรางวัลสำเร็จ',
        text: reward.type === 'item' ? 'ระบบบันทึกคำขอแลกรางวัลแล้ว กรุณารอแอดมินดำเนินการ' : undefined,
        confirmButtonColor: '#d33',
      })
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'แลกรางวัลไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#d33',
      })
    } finally {
      setSubmittingRewardId(null)
    }
  }

  return (
    <div className="point-page-wrapper">
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h4 className="mb-1">แลกรางวัล</h4>
                    <small className="text-muted">ใช้คะแนนสะสมเพื่อแลกเครดิตหรือของรางวัล</small>
                  </div>
                  <a
                    href="#"
                    className="txt-link-d"
                    onClick={(e) => {
                      e.preventDefault()
                      navigate('/dashboard')
                    }}
                  >
                    <i className="fas fa-chevron-left me-1"></i>
                    หน้าหลัก
                  </a>
                </div>

                <div className="nav nav-pills nav-pills-ticket mx--1 mb-2" role="tablist">
                  <div className="nav-link col px-1 py-0">
                    <Link to="/points/moneytopoint" className="check-button w-100 d-block text-decoration-none bgc-4 c-dark">
                      <h4><i className="fas fa-wallet"></i></h4>
                      <p>เงินเป็นคะแนน</p>
                    </Link>
                  </div>
                  <div className="nav-link col px-1 py-0">
                    <Link to="/points/pointtomoney" className="check-button w-100 d-block text-decoration-none bgc-4 c-dark">
                      <h4><i className="fas fa-exchange-alt"></i></h4>
                      <p>คะแนนเป็นเงิน</p>
                    </Link>
                  </div>
                  <div className="nav-link col px-1 py-0 active">
                    <Link to="/points/rewards" className="check-button w-100 d-block text-decoration-none bgg-5 c-white">
                      <h4><i className="fas fa-gift"></i></h4>
                      <p>ของรางวัล</p>
                    </Link>
                  </div>
                </div>

                <div className="row mx--1 mb-2">
                  <div className="col-12 col-md-4 px-1 mb-2">
                    <div className="bgc-4 bdr-5 p-2 h-100 text-center">
                      <p className="c-dark mb-1">คะแนนสะสมปัจจุบัน</p>
                      <h5 className="c-red t-medium mb-0">{pointsText}</h5>
                    </div>
                  </div>
                  <div className="col-12 col-md-4 px-1 mb-2">
                    <div className="bgc-4 bdr-5 p-2 h-100 text-center">
                      <p className="c-dark mb-1">เครดิตคงเหลือ</p>
                      <h5 className="c-main t-medium mb-0">{balanceText}</h5>
                    </div>
                  </div>
                  <div className="col-12 col-md-4 px-1 mb-2">
                    <div className="bgc-4 bdr-5 p-2 h-100 text-center">
                      <p className="c-dark mb-1">สถานะระบบแลกรางวัล</p>
                      <h6 className={`mb-0 t-medium ${rewardsEnabled && activeRewards.length > 0 ? 'c-green' : 'c-red'}`}>
                        {isLoadingConfig
                          ? 'กำลังโหลด...'
                          : rewardsEnabled && activeRewards.length > 0
                            ? 'พร้อมใช้งาน'
                            : rewardsEnabled
                              ? 'ยังไม่มีรางวัลที่เปิดใช้งาน'
                              : 'ปิดระบบแลกรางวัล'}
                      </h6>
                    </div>
                  </div>
                </div>

                {rewardsEnabled && activeRewards.length > 0 ? (
                  <div className="row mx--1">
                    {activeRewards.map((reward) => (
                      <div key={reward.id} className="col-12 col-md-6 px-1 mb-2">
                        <div className="bgc-4 bdr-5 p-2 h-100 d-flex flex-column">
                          <div className="wrap align-items-center mb-2">
                            <div className="img-b bdr-5 bgc-7">
                              <img src={reward.imageUrl || getImageUrl('logo-m.png')} alt={reward.name} />
                            </div>
                            <div className="txt-b ms-3">
                              <h6 className="c-dark mb-1">{reward.name}</h6>
                              <h5 className="c-red t-medium mb-1">{formatNumber(reward.pointsCost)} คะแนน</h5>
                              <p className="c-dark fs-14 mb-0">
                                {reward.type === 'credit'
                                  ? `รับเครดิต ${formatNumber(reward.creditAmount)} บาท`
                                  : reward.description || 'ของรางวัล'}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="check-button w-100 border-0 c-white mt-auto bgg-5"
                            onClick={() => void handleRedeem(reward)}
                            disabled={submittingRewardId === reward.id}
                          >
                            <i className="fas fa-gift me-2"></i>
                            {submittingRewardId === reward.id ? 'กำลังทำรายการ...' : 'แลกรางวัล'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bgc-4 bdr-5 p-3 text-center">
                    <h6 className="c-dark mb-1">ยังไม่พร้อมใช้งาน</h6>
                    <p className="c-dark mb-0">
                      {isLoadingConfig
                        ? 'กำลังโหลดรายการรางวัล...'
                        : rewardsEnabled
                          ? 'ยังไม่มีรางวัลที่เปิดให้แลกในขณะนี้'
                          : 'ระบบแลกรางวัลถูกปิดอยู่'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PointsRewardsPage
